import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { Eye, Flag, TriangleAlert } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useAuthor } from '@/hooks/useAuthor';
import { useProbedDuration } from '@/hooks/useProbedDuration';
import {
  formatDuration,
  getNIPVersionLabel,
  getVideoNip19,
  isYouTubeUrl,
  timeAgo,
  type VideoData,
} from '@/lib/videoTypes';
import { genUserName } from '@/lib/genUserName';
import { cn } from '@/lib/utils';

interface VideoCardProps {
  video: VideoData;
  reported?: boolean;
  /** 'shorts' renders a dense 9:16 vertical card for the Shorts grid. */
  variant?: 'default' | 'shorts';
}

/** Observe when the card scrolls into view (for lazy duration probing). */
function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        }
      },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, inView };
}

export function VideoCard({ video, reported, variant = 'default' }: VideoCardProps) {
  const { ref, inView } = useInView();
  const author = useAuthor(video.pubkey);
  const metadata = author.data?.metadata;
  const displayName = metadata?.name ?? genUserName(video.pubkey);

  const probed = useProbedDuration(video, inView);
  const isYouTube = isYouTubeUrl(video.videoUrl);

  const [showContent, setShowContent] = useState(false);
  const hasCw = !!video.contentWarning;

  const link = `/${getVideoNip19(video)}`;
  const isShorts = variant === 'shorts';

  return (
    <div ref={ref} className="group flex flex-col gap-2 min-w-0">
      <div className={cn(
        'relative overflow-hidden rounded-xl bg-muted',
        isShorts
          ? 'aspect-[9/16] w-full'
          : video.isVertical
            ? 'aspect-[9/16] max-w-[220px]'
            : 'aspect-video',
      )}>
        {hasCw && !showContent ? (
          <button
            type="button"
            onClick={() => setShowContent(true)}
            className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted/95 text-center p-4 transition-colors hover:bg-muted"
          >
            <TriangleAlert className="h-8 w-8 text-yellow-500" />
            <span className="text-sm font-semibold">Content Warning</span>
            {video.contentWarning && (
              <span className="text-xs text-muted-foreground line-clamp-2">{video.contentWarning}</span>
            )}
            <span className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-foreground">
              <Eye className="h-3.5 w-3.5" /> Show anyway
            </span>
          </button>
        ) : (
          <Link to={link} className="block h-full w-full">
            {video.thumbnailUrl ? (
              <img
                src={video.thumbnailUrl}
                alt={video.title}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-muted to-accent">
                <span className="text-4xl opacity-40">🎬</span>
              </div>
            )}
          </Link>
        )}

        {/* Duration badge */}
        {(!hasCw || showContent) && (
          <span className="absolute bottom-1.5 right-1.5 rounded bg-black/80 px-1.5 py-0.5 text-[11px] font-medium text-white tabular-nums">
            {probed.status === 'known'
              ? formatDuration(probed.duration)
              : probed.status === 'probing'
                ? '…'
                : isYouTube
                  ? 'YT'
                  : '??:??'}
          </span>
        )}

        {reported && (
          <span className="absolute top-1.5 left-1.5" title="This video has kind 1984 reports">
            <Badge variant="destructive" className="gap-1 px-1.5 py-0.5 text-[10px]">
              <Flag className="h-3 w-3" /> Reported
            </Badge>
          </span>
        )}
      </div>

      <div className="flex gap-2.5 min-w-0">
        {!isShorts && (
          <AuthorAvatar pubkey={video.pubkey} picture={metadata?.picture} name={displayName} />
        )}
        <div className="min-w-0 flex-1">
          <Link to={link} className="block">
            <h3 className={cn(
              'font-medium leading-snug line-clamp-2 group-hover:text-primary transition-colors',
              isShorts ? 'text-sm' : 'text-[15px]',
            )}>
              {video.title}
            </h3>
          </Link>
          <AuthorName pubkey={video.pubkey} name={displayName} nip05={metadata?.nip05} />
          {!isShorts && (
            <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
              <span>{timeAgo(video.createdAt)}</span>
              <span aria-hidden>·</span>
              <span title={getNIPVersionLabel(video.nipVersion)}>
                {video.nipVersion === 'nip71-regular'
                  ? 'Regular'
                  : video.nipVersion === 'nip71-addressable'
                    ? 'Addressable'
                    : video.nipVersion === 'note-video'
                      ? 'Note'
                      : 'Legacy'}
              </span>
              {isYouTube && (
                <>
                  <span aria-hidden>·</span>
                  <span className="text-yellow-600 dark:text-yellow-500 font-medium">YouTube link</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AuthorAvatar({ pubkey, picture, name }: { pubkey: string; picture?: string; name: string }) {
  const npub = nip19.npubEncode(pubkey);
  return (
    <Link to={`/${npub}`} className="shrink-0 mt-0.5">
      <Avatar className="h-9 w-9">
        <AvatarImage src={picture} alt={name} loading="lazy" />
        <AvatarFallback>{name.slice(0, 2).toUpperCase()}</AvatarFallback>
      </Avatar>
    </Link>
  );
}

function AuthorName({ pubkey, name, nip05 }: { pubkey: string; name: string; nip05?: string }) {
  const npub = nip19.npubEncode(pubkey);
  return (
    <Link to={`/${npub}`} className="block truncate text-[13px] text-muted-foreground hover:text-foreground transition-colors mt-0.5">
      {name}
      {nip05 && <span className="ml-1 text-[11px] opacity-70">✓</span>}
    </Link>
  );
}
